<template>
  <etoro-progress-loader
    v-if="showContentLoader"
    :job-id="otsJobConfig.jobId"
    :progress-state="progressState"
  />
  <div
    v-else
    class="page"
  >
    <riseup-header :flow-config-id="otsJobConfig.flowConfigId" />
    <div
      v-if="showLoader"
      class="loader-wrapper"
    >
      <tiny-loader
        class="loader-class"
        variant="discount"
      />
    </div>
    <div v-else-if="creds.length > 0 || isPolling">
      <div class="content ready">
        <img
          src="../../../assets/etoro/etoro-connect-ready-to-go.svg"
          class="etoro-connect-ready"
        >
        <span class="text center subtitle">
          Your account is connected so we're ready to start building your personal summaries
        </span>
        <div class="center">
          <button
            class="green-button"
            @click="handleGoToInsightsClick"
          >
            See your money insights
            <span>→</span>
          </button>
          <button
            class="outline-green-button"
            @click="handleConnectAccountsClick"
          >
            Connect another account
          </button>
          <p class="below-buttons">
            You can also connect more accounts later on
          </p>
          <div class="separator-line" />
          <p class="complience-text">
            RiseUp may not have visibility of debt or adverse financial situations.
            You should always consider your personal circumstances and your ability to
            bear losses when investing, including any vulnerabilities. More info
            <a href="https://www.etoro.com/customer-service/vulnerability-program">here</a>.
            The information provided is not advice, and neither RiseUp nor eToro assume
            any liability for any actions taken by users as a result of the information provided.
          </p>
          <ComplianceText />
        </div>
      </div>
    </div>
    <div
      v-else
      class="else-content"
    >
      <div class="content">
        <div class="center">
          <img
            src="../../../assets/etoro/etoro-connect-title.svg"
            class="etolro-connect-tite"
          >
          <div
            class="bullets-container"
          >
            <div class="bullet">
              <img
                src="../../../assets/green_check.svg"
                alt="Checkmark Icon"
              >
              <p class="text">
                To get the best results, connect the account you use most for spending.
              </p>
            </div>
            <div class="bullet">
              <img
                src="../../../assets/green_check.svg"
                alt="Checkmark Icon"
              >
              <p class="text">
                Get personalised summaries in minutes.
              </p>
            </div>
          </div>
          <div class="flex-container">
            <div
              class="etoro-flex-box"
            >
              <div class="icon">
                <img
                  src="../../../assets/etoro/etoro-lock.svg"
                  alt="Shield Icon"
                >
              </div>
              <div class="text">
                <span>We only connect once to create your summary.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="footer">
        <button
          class="green-button"
          @click="handleConnectAccountsClick"
        >
          Connect accounts to get insights
          <span>→</span>
        </button>
        <ComplianceText />
      </div>
    </div>
  </div>
  <error-toast
    v-if="showErrorToast"
    text="Failed to connect your account."
    bold-text="Please try connecting again."
    reason="truelayer_authentication_failed"
    @close="showErrorToast = false"
    @bold-text-click="handleConnectAccountsClick"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Segment from '../../../Segment';
import { getCustomerCredentials } from '../../../api/cashflowApi';
import { authenticateTrueLayerCode, getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import { useProgressTracking } from '../../../composables/useProgressTracking';
import config from '../../../config';
import { useCashflow } from '../../../services/cashflowService';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import { POLLING_CONFIG } from '../../../types/insight';
import { _descopeAutheticate } from '../../../utils/descopeUtils';
import ComplianceText from '../../Common/ComplianceText.vue';
import ErrorToast from '../../Common/ErrorToast.vue';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import EtoroProgressLoader from './EtoroProgressLoader.vue';

const sessionStore = useSessionStore();
const { isPolling, updateIsPolling, startPollingByJobId } = useInsights();
const { progressState, updateProgress, reset, setError } = useProgressTracking();
const router = useRouter();
const route = useRoute();
const { toggleLoader, showContentLoader } = useContentLoader();
Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);
const { getOtsJobConfig } = useOtsJob();

const otsJobConfig = await getOtsJobConfig();
const { loadCashflowData } = useCashflow();
const creds = ref([]);
const showLoader = ref(false);
const showErrorToast = ref(false);

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('EtoroConnectOtsAccountsConnectClicked', { gclid: Cookies.get('gclid') });
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId }, config.get().otsEtoroEnv);
};

const handleGoToInsightsClick = async () => {
  Segment.trackUserGot('EtoroConnectOtsAccountsGetInsightsClicked', { gclid: Cookies.get('gclid') });
  reset();
  toggleLoader(true);
  startPollingByJobId(
    otsJobConfig.jobId,
    POLLING_CONFIG.MAX_ATTEMPTS,
    POLLING_CONFIG.POLLING_INTERVAL,
    pollingSuccess,
    handlePollingError,
    updateProgress,
  );
};

const pollingSuccess = async () => {
  Segment.trackUserGot('EtoroOtsInsightsReady');

  const queryParam = route.query || null;
  const shouldReload = queryParam?.fromSuccess === 'true';
  await loadCashflowData(6);
  router.push({
    path: '/ots/etoro/success',
    state: shouldReload ? { shouldReload: true } : undefined,
  });
};

const handlePollingError = () => {
  setError('We encountered an issue processing your data. Please try connecting your accounts again.');
  Segment.trackUserGot('EtoroOtsProcessingFailed');
  router.push('/ots/etoro/connect');
};

onBeforeMount(async () => {
  toggleLoader(false);
  showLoader.value = true;
  const queryParam = route.query || null;
  if (queryParam.token) {
    const isDescopeAutheticated = await _descopeAutheticate(queryParam.token);
    if (isDescopeAutheticated) {
      const sessionStore = useSessionStore();
      await sessionStore.init();
    }
  }

  creds.value = await getCustomerCredentials();

  if (queryParam.code) {
    updateIsPolling(true);
    try {
      const response = await authenticateTrueLayerCode(queryParam.code, router);
      if (!response) {
        showErrorToast.value = true;
        updateIsPolling(false);
      }
    } catch (error) {
      showErrorToast.value = true;
      updateIsPolling(false);
      console.error('TrueLayer authentication failed:', error);
    }
  }
  showLoader.value = false;

  Segment.trackUserGot('EtoroConnectOtsAccountPageEntered');
});

</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.page {
  background-color: #F6F8ED;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  scroll-behavior: smooth;
  width: 90%;
  margin: 0 auto;
  margin-top: 45px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.content h1 {
  color: #202125;
  font-size: 28px;
  font-family: GrenetteSemiBold;
  font-weight: 600;
  line-height: 39px;
  margin-bottom: 10px;
}

.content .text {
  color: #202125;
  font-size: 18px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 28px;
  margin-bottom: 20px;
}

.content .subtitle {
  margin-top: 16px;
}
.hands {
  width: 200px;
  align-self: center;
  margin-top: 22px;
}

p.below-buttons {
  color: #202125;
  font-size: 14px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 22px;
  word-wrap: break-word;
  margin-top: 5px;
}

.flex-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  margin-top: 40px;
}

.etoro-flex-box {
  padding: 15px 15px 15px;
  background: rgba(19, 198, 54, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 100%;
}

.etoro-flex-box .text {
  margin: 0 auto;
  text-align: left;
  font-size: 15px;
  line-height: normal;

}

.etoro-flex-box img {
  width: 30px;
  height: 30px;
}

.icon {
  margin-right: 20px;
  display: flex;
  align-items: center;
}

.back-arrow-container {
  padding-left: 21px;
  padding-top: 21px;
  cursor: pointer;
}

.back-arrow {
  width: 24px;
  height: 24px;
}

.footer {
  width: 100%;
  text-align: center;
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 40px;
}

.green-button {
  background-color: rgba(19, 198, 54, 1);
  border-radius: 13px;
  color: white;
  padding: 19px 20px;
  border: none;
  margin: 10px 0;
  width: 90%;
  cursor: pointer;
  font-size: 16px;
  font-family: CircularStdBook;
  font-weight: 700;
  align-self: center;
}

p.complience-text {
color: rgba(0, 0, 0, 0.48);
 font-size: 12px;
 font-family: CircularStdBook;
 font-weight: 450;
 line-height: 18px;
 word-wrap: break-word;
 text-align: left;
 width: 90%;
 margin: 20px auto;
}

.outline-green-button{
  color: rgba(19, 198, 54, 1);
  background-color: transparent;
  border-radius: 13px;
  padding: 19px 20px;
  border: 1px solid rgba(19, 198, 54, 1);
  margin: 10px 0;
  width: 90%;
  cursor: pointer;
  font-size: 16px;
  font-family: CircularStdBook;
  font-weight: 700;
}

.bullets-container {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.bullet {
  display: flex;
  align-items: center;
  gap: 15px;
}

.bullet img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.bullet .text {
  text-align: left;
  font-size: 15px;
  line-height: normal;
  display: flex;
  align-items: flex-start;
  margin: 0;
}

.consent {
  padding-bottom: 10px;
  font-size: 14px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 22px;
  word-wrap: break-word;
  color: #888888;
}

.creds-container {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
}

.etoro-connect-title {
  width: 100%;
  padding: 0px 30px;
  margin-top: 40px;
  margin-bottom: 20px;
}

.etoro-connect-ready {
  width: 100%;
  padding: 0px 60px;
  // padding-top: 60px;
}

.cred-box {
  display: flex;
  align-items: center;
  padding: 10px;
  margin: 10px 0;
  background-color: #f0f0f0;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.cred-logo {
  width: 30px;
  height: 30px;
  margin-right: 10px;
}

.cred-title {
  font-size: 16px;
  color: #333;
}
.ready {
  gap: 15px;
}

.center {
  text-align: center;
}

.else-content {
  overflow-y: auto;
  scroll-behavior: smooth;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.separator-line {
  width: 95%;
  height: 0px;
  opacity: 0.20;
  border: 1px black solid;
  margin: 30px auto;
  margin-top: 55px;
}

</style>
